/**
 * useKeyboardAwareScroll
 *
 * On focus, CENTERS the focused input in the visible area above the keyboard. Triggered
 * by the `focusin` itself, so it works field-to-field with the keyboard already open
 * (no viewport size change → no resize/viewportChanged fires in that case).
 *
 * IMPORTANT — listeners live on `document`, NOT the scroll container. The container can
 * mount AFTER this run-once effect fires (e.g. DetailsPage renders a loading spinner
 * first, so the `.scroll` ref is still null when the effect runs; the deps never change,
 * so the effect never re-runs to pick up the late container). Binding to `document`
 * sidesteps that entirely — the handlers read `containerRef.current` fresh and gate on
 * `container.contains(target)`, so only fields inside OUR scroller act.
 *
 * Visible bottom = the keyboard top, taken as the MINIMUM of the live sources
 * (containerRect.bottom — the scroller is sized to --app-viewport-height — plus
 * visualViewport and Telegram.WebApp.viewportHeight as a safety net for the frame where
 * the CSS height lags). The field's center is moved to the visible center with an
 * immediate `scrollTop +=` (a smooth scroll is cancelled by the keyboard animation).
 *
 * Re-centers when the keyboard finishes opening (Telegram `viewportChanged` with
 * isStateStable, or visualViewport.resize), with a ~400ms fallback for clients that emit
 * neither.
 *
 * Returns an imperative handle for focus we CAN'T observe through the DOM: a cross-origin
 * element (a Stripe Element iframe) reports its own focus via a component callback, not a
 * document `focusin`. The page forwards that callback here with the element's host node,
 * which runs the exact same centering as a native field focus.
 */

import { useEffect, useMemo, useRef, RefObject } from 'react';
import { useViewportSource } from './viewportSource';

/** ms fallback to re-center after the keyboard settles when no viewport event arrives. */
const STABLE_FALLBACK_MS = 400;

/** The last keyboard height measured this session: the next first focus predicts it instead of the
 * estimate, so the rise does not step up again when the real height arrives (phone 2026-10-03). */
const KEYBOARD_PX_KEY = 'telify:keyboard-px';
export function rememberedKeyboardPx(): number {
  try {
    const px = Number(sessionStorage.getItem(KEYBOARD_PX_KEY));
    return Number.isFinite(px) && px > 0 ? px : 0;
  } catch {
    return 0;
  }
}
export function rememberKeyboardPx(px: number): void {
  try {
    sessionStorage.setItem(KEYBOARD_PX_KEY, String(Math.round(px)));
  } catch {
    // Storage blocked: the estimate stays the first guess.
  }
}

interface UseKeyboardAwareScrollOptions {
  /** Retained for API compatibility; centering uses the full visible band. */
  topPadding?: number;
  /** Retained for API compatibility. */
  bottomPadding?: number;
  /** Retained for API compatibility. */
  scrollDelay?: number;
  /** When false, the hook is inert (no listeners). Default: true. */
  enabled?: boolean;
  /**
   * Optional stable-shell target that receives `--keyboard-aware-inset`. On touch
   * focus the hook writes a predicted keyboard height immediately, before the
   * late Telegram/visualViewport event arrives. Fixed/absolute action bars and
   * scroll spacers can consume the same inset without a React render hop.
   */
  keyboardInsetTargetRef?: RefObject<HTMLElement | null>;
  /** First-focus keyboard estimate used until live viewport geometry arrives. */
  keyboardEstimatePx?: number;
}

export interface KeyboardAwareScrollHandle {
  /**
   * Center the scroller on `el` as if it had received focus. For focus the DOM can't
   * surface — a cross-origin Stripe Element iframe fires its own `onFocus`, never a
   * document `focusin` on an inner input — the page forwards that event here with the
   * Element's host node. Runs the SAME centering + keyboard-settle re-center + fallback
   * as a real field focus. No-op when the hook is disabled or `el` is null/outside the
   * scroller.
   */
  focusElement: (el: HTMLElement | null) => void;
  /** Clear the externally-supplied focus target (mirror of a field blur). */
  blurElement: () => void;
}

export function useKeyboardAwareScroll(
  containerRef: RefObject<HTMLElement | null>,
  options: UseKeyboardAwareScrollOptions = {}
): KeyboardAwareScrollHandle {
  const {
    enabled = true,
    keyboardInsetTargetRef,
    keyboardEstimatePx = 0,
  } = options;

  // Imperative bridge — the effect installs live implementations while mounted; the
  // returned handle (stable identity) forwards through these refs so callers never see
  // it change across re-renders. When disabled/unmounted they fall back to no-ops.
  const focusElementRef = useRef<(el: HTMLElement | null) => void>(() => {});
  const blurElementRef = useRef<() => void>(() => {});
  // Telegram's viewport inside the Mini App, none on the website (viewportSource.ts).
  const getViewport = useViewportSource();

  useEffect(() => {
    if (!enabled) return;

    const predictiveInsetEnabled =
      keyboardEstimatePx > 0 &&
      (navigator.maxTouchPoints ?? 0) > 0;
    const writeKeyboardInset = (px: number) => {
      keyboardInsetTargetRef?.current?.style.setProperty(
        '--keyboard-aware-inset',
        `${Math.max(0, px)}px`,
      );
    };
    const readKeyboardInset = () => {
      const target = keyboardInsetTargetRef?.current;
      if (!target) return 0;
      const value = Number.parseFloat(
        target.style.getPropertyValue('--keyboard-aware-inset'),
      );
      return Number.isFinite(value) ? Math.max(0, value) : 0;
    };
    /** A measured height: written, and remembered for the next first focus. */
    const writeMeasuredInset = (px: number) => {
      rememberKeyboardPx(px);
      writeKeyboardInset(px);
    };
    const readMeasuredKeyboardInset = () => {
      const cssValue = Number.parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--app-keyboard-height'),
      );
      const vv = window.visualViewport;
      let visibleBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
      const tgHeight = getViewport()?.viewportHeight;
      if (typeof tgHeight === 'number' && tgHeight > 0) {
        visibleBottom = Math.min(visibleBottom, tgHeight);
      }
      const geometryValue = Math.max(0, window.innerHeight - visibleBottom);
      return Math.max(Number.isFinite(cssValue) ? cssValue : 0, geometryValue);
    };

    /**
     * `input[type=time]` opens a native PICKER, not the text keyboard.
     *
     * It shrinks the visible viewport just like a keyboard does, so every part of
     * this hook used to treat it as one: the pinned action bar rose to meet the
     * picker and dropped again on dismiss, so Cancel and Save jumped every time an
     * admin touched an hour in the weekly editor (device smoke, 2026-08-13). A
     * picker is a self-contained overlay — nothing needs to move out of its way.
     *
     * Time fields are therefore not keyboard-bearing HERE: no predicted inset, no
     * centering, and `reactToViewport` leaves the inset alone while one holds
     * focus (the picker's own resize would otherwise re-raise the bar). Text
     * inputs and textareas are untouched — the keyboard DoD is theirs.
     */
    // Dates are pickers too: `input[type=date]` (and its month/week/datetime-local kin) opens the
    // native wheel on iOS, never the text keyboard, so the pinned Save pill must not rise for it
    // (form builder Validation dates, device smoke 2026-09-03: "the save button jumps up").
    const NATIVE_PICKER_TYPES = new Set(['time', 'date', 'datetime-local', 'month', 'week']);
    const isNativePickerField = (el: Element | null): boolean =>
      !!el && el.tagName === 'INPUT' && NATIVE_PICKER_TYPES.has((el as HTMLInputElement).type);

    const isFormElement = (el: Element | null): el is HTMLElement =>
      !!el &&
      !isNativePickerField(el) &&
      (el.tagName === 'INPUT' ||
        el.tagName === 'TEXTAREA' ||
        el.getAttribute('contenteditable') === 'true');

    // Captured at focus time — WKWebView can blur/refocus during the keyboard
    // animation, so we don't depend solely on document.activeElement later.
    let focusedEl: HTMLElement | null = null;
    // True while a native picker field inside our scroller holds focus. The
    // picker shrinks the viewport, so without this the resize handler below would
    // write the inset the focus path deliberately skipped.
    let pickerHasFocus = false;

    const centerFocused = () => {
      const container = containerRef.current; // read fresh — may have mounted after this effect ran
      if (!container) return;

      const target =
        focusedEl && container.contains(focusedEl)
          ? focusedEl
          : isFormElement(document.activeElement) && container.contains(document.activeElement)
            ? (document.activeElement as HTMLElement)
            : null;
      if (!target) return;

      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();

      // Visible bottom = keyboard top. The scroller is sized to --app-viewport-height so
      // containerRect.bottom already shrinks to it; min with the live viewport values
      // covers the frame where the CSS height lags.
      const candidates = [containerRect.bottom];
      const vv = window.visualViewport;
      if (vv) candidates.push(vv.offsetTop + vv.height);
      const tgHeight = getViewport()?.viewportHeight;
      if (typeof tgHeight === 'number' && tgHeight > 0) candidates.push(tgHeight);
      // Stable-height own-scroll pages do not shrink their container while the
      // keyboard opens. Their predictive inset supplies the missing first-frame
      // keyboard top so focus centering starts with the keyboard animation.
      const predictiveInset = readKeyboardInset();
      if (predictiveInset > 0) candidates.push(containerRect.bottom - predictiveInset);
      const visibleBottom = Math.min(...candidates);

      // Move the field's center to the center of the visible band. Smooth — field-to-field
      // has stable geometry so it won't be interrupted, and the keyboard-open re-center
      // fires after the animation settles. Absolute target (not a relative scrollBy) so a
      // re-trigger mid-animation continues to the same place instead of compounding; the
      // browser clamps to the scroll range.
      const visibleCenter = (containerRect.top + visibleBottom) / 2;
      const targetCenter = (targetRect.top + targetRect.bottom) / 2;
      const delta = targetCenter - visibleCenter;
      if (Math.abs(delta) > 1) {
        container.scrollTo({ top: container.scrollTop + delta, behavior: 'smooth' });
      }
    };

    // Two frames so layout has settled, and de-duped — every trigger cancels the prior.
    let rafId1 = 0;
    let rafId2 = 0;
    let fallbackId: ReturnType<typeof setTimeout> | undefined;
    let insetResetId: ReturnType<typeof setTimeout> | undefined;
    let noShowId: ReturnType<typeof setTimeout> | undefined;
    const scheduleCenter = () => {
      cancelAnimationFrame(rafId1);
      cancelAnimationFrame(rafId2);
      rafId1 = requestAnimationFrame(() => {
        rafId2 = requestAnimationFrame(centerFocused);
      });
    };
    const clearFallback = () => {
      if (fallbackId !== undefined) clearTimeout(fallbackId);
      fallbackId = undefined;
    };
    const clearInsetReset = () => {
      if (insetResetId !== undefined) clearTimeout(insetResetId);
      insetResetId = undefined;
    };
    const clearNoShow = () => {
      if (noShowId !== undefined) clearTimeout(noShowId);
      noShowId = undefined;
    };

    // Shared focus entry — a native `focusin` AND the imperative `focusElement` both
    // route here so the Stripe surface gets byte-for-byte the same treatment as a form
    // field.
    const beginFocus = (el: HTMLElement) => {
      const container = containerRef.current;
      if (!container || !container.contains(el)) return; // only elements inside OUR scroller
      focusedEl = el;
      clearInsetReset();
      clearNoShow();
      if (predictiveInsetEnabled) {
        const measured = readMeasuredKeyboardInset();
        if (measured > 0) writeMeasuredInset(measured);
        else writeKeyboardInset(rememberedKeyboardPx() || keyboardEstimatePx);
        // Touch device + hardware keyboard: if no viewport shrink materialises,
        // remove the optimistic inset instead of leaving the footer floating.
        if (measured === 0) {
          noShowId = setTimeout(() => {
            noShowId = undefined;
            if (readMeasuredKeyboardInset() === 0) {
              writeKeyboardInset(0);
              scheduleCenter();
            }
          }, 700);
        }
      }
      // Center NOW — field-to-field with the keyboard already open fires no viewport
      // event, so this is the only trigger in that (common) case.
      scheduleCenter();
      // ...and once more after the keyboard settles, for the open-from-closed case.
      clearFallback();
      fallbackId = setTimeout(() => {
        fallbackId = undefined;
        scheduleCenter();
      }, STABLE_FALLBACK_MS);
    };

    const handleFocusIn = (e: FocusEvent) => {
      const t = e.target as Element | null;
      if (isNativePickerField(t)) {
        pickerHasFocus = !!containerRef.current?.contains(t as Node);
        return;
      }
      pickerHasFocus = false;
      if (!isFormElement(t)) return;
      beginFocus(t as HTMLElement);
    };
    const handleFocusOut = (e: FocusEvent) => {
      const container = containerRef.current;
      const next = e.relatedTarget as Element | null;
      // Moving text field → time field still runs the reset below: the text
      // keyboard IS closing and the inset must fall with it. The flag only stops
      // the picker's own viewport shrink from putting the inset back.
      pickerHasFocus = isNativePickerField(next) && !!container?.contains(next as Node);
      if (container && isFormElement(next) && container.contains(next)) return;
      clearInsetReset();
      insetResetId = setTimeout(() => {
        insetResetId = undefined;
        const active = document.activeElement;
        if (container && isFormElement(active) && container.contains(active)) return;
        focusedEl = null;
        clearFallback(); // no pending settle re-center should outlive the focus
        clearNoShow();
        writeKeyboardInset(0);
      }, 60);
    };

    // Re-center on a keyboard-settle event ONLY while the keyboard is arriving/holding
    // (the visible viewport is shrinking or steady), NEVER when it GROWS. On collapse the
    // viewport grows back to full; a Stripe card field can keep focus through the dismiss
    // (no `focusout`/blur fires), so a re-center here would drag the still-tracked field
    // to the middle of the now-full screen — the "second jump" ~½s after the keyboard
    // closes. Direction, not an absolute keyboard threshold, is the reliable cross-client
    // signal. (Native inputs usually blur on collapse and clear the target anyway, but the
    // guard fixes it uniformly — no per-page fork.)
    let lastTgHeight = getViewport()?.viewportHeight ?? Number.POSITIVE_INFINITY;
    let lastVvHeight = window.visualViewport?.height ?? Number.POSITIVE_INFINITY;

    const reactToViewport = (grew: boolean) => {
      clearFallback(); // either direction cancels a pending settle timer
      // A native picker is not the keyboard. Leave the inset — and the pinned
      // action bar that reads it — exactly where they are while one is open.
      if (pickerHasFocus) return;
      if (predictiveInsetEnabled) {
        const measured = readMeasuredKeyboardInset();
        if (grew) {
          // Collapsing. The measured height LAGS the animation: the app keyboard
          // variable holds the last open height and the geometry still reads the
          // half-dismissed keyboard, so writing it here sent a pinned action bar
          // back UP after the blur had already dropped it, then stepped it down
          // one resize event at a time (Booking rules, phone 2026-09-18). Growth
          // never raises the inset; it only confirms zero once the keyboard is
          // fully gone (the no-blur Stripe case still lands at zero).
          if (measured === 0) writeKeyboardInset(0);
        } else if (measured > 0) {
          clearNoShow();
          writeMeasuredInset(measured);
        }
      }
      if (grew) return; // keyboard collapsing — leave the scroll where it is
      scheduleCenter();
    };

    const handleTelegramViewport = (event?: unknown) => {
      if (!(event as { isStateStable?: boolean } | undefined)?.isStateStable) return;
      const h = getViewport()?.viewportHeight ?? lastTgHeight;
      const grew = h > lastTgHeight;
      lastTgHeight = h;
      reactToViewport(grew);
    };
    const handleBrowserResize = () => {
      const h = window.visualViewport?.height ?? lastVvHeight;
      const grew = h > lastVvHeight;
      lastVvHeight = h;
      reactToViewport(grew);
    };

    // document/window — always available at effect time (the container may not be).
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusOut, true);
    const vv = window.visualViewport;
    if (vv) vv.addEventListener('resize', handleBrowserResize);
    const webApp = getViewport();
    if (webApp) webApp.onEvent('viewportChanged', handleTelegramViewport);

    // Expose the imperative bridge while mounted.
    focusElementRef.current = (el) => {
      if (el) beginFocus(el);
    };
    blurElementRef.current = () => {
      focusedEl = null;
      pickerHasFocus = false;
      clearFallback();
      clearNoShow();
      writeKeyboardInset(0);
    };

    return () => {
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('focusout', handleFocusOut, true);
      if (vv) vv.removeEventListener('resize', handleBrowserResize);
      if (webApp) webApp.offEvent('viewportChanged', handleTelegramViewport);
      clearFallback();
      clearInsetReset();
      clearNoShow();
      writeKeyboardInset(0);
      cancelAnimationFrame(rafId1);
      cancelAnimationFrame(rafId2);
      focusElementRef.current = () => {};
      blurElementRef.current = () => {};
    };
  }, [containerRef, enabled, getViewport, keyboardEstimatePx, keyboardInsetTargetRef]);

  return useMemo(
    () => ({
      focusElement: (el: HTMLElement | null) => focusElementRef.current(el),
      blurElement: () => blurElementRef.current(),
    }),
    []
  );
}
